import {getCurrentUserLogin} from '@github-ui/client-env'

import {usePullRequestState} from '../../../../hooks/use-pull-request-state'
import {useReviewers} from '../../data/use-reviewers'
import {PullRequestState} from '../../../../types'
import type {PendingReviewRequest} from '@github-ui/dotcom-schema/types/pull_requests/v1/review_requests'
import type {Reviewer} from '@github-ui/dotcom-schema/types/pull_requests/v1/reviewer'

export type ReviewRequestedKind = 'direct' | 'codeowner' | 'delegated'

export type ReviewRequestedData = {
  kind: ReviewRequestedKind
  /** Teams the review was delegated from (only for the `delegated` kind). */
  delegatedFromTeams: Reviewer[]
}

export type ReviewRequestedQueryResult = {
  data: ReviewRequestedData | null
  queryStatus: 'loading' | 'error' | 'missing' | 'success'
}

function isPendingRequestForViewer(request: PendingReviewRequest, viewerLogin: string): boolean {
  const reviewer = request.reviewer
  return reviewer?.reviewerType === 'User' && reviewer.displayLogin === viewerLogin
}

/**
 * Pure classification of the viewer's requested-review state. Returns `null` when the
 * viewer is not a requested reviewer. Codeowner and delegation add context to a direct
 * request.
 */
export function selectReviewRequested(
  pendingReviewRequests: PendingReviewRequest[],
  viewerLogin: string,
): ReviewRequestedData | null {
  const request = pendingReviewRequests.find(pending => isPendingRequestForViewer(pending, viewerLogin))
  if (!request) return null

  if (request.isCodeOwnerFromDelegation && request.delegatedFromTeam) {
    return {kind: 'delegated', delegatedFromTeams: [request.delegatedFromTeam]}
  }

  if (request.isCodeOwner) {
    return {kind: 'codeowner', delegatedFromTeams: []}
  }

  return {kind: 'direct', delegatedFromTeams: []}
}

/**
 * Surfaces the viewer's requested-review state for the "Needs attention" section, or
 * `null` when there is nothing to show. Only open, non-queued pull requests qualify.
 */
export function useReviewRequestedData(): ReviewRequestedQueryResult {
  const pullRequestState = usePullRequestState()
  const {data: reviewers, isError, isLoading} = useReviewers()
  const viewerLogin = getCurrentUserLogin()

  if (!viewerLogin || pullRequestState !== PullRequestState.Open) {
    return {data: null, queryStatus: 'success'}
  }

  if (!reviewers) {
    return {data: null, queryStatus: isLoading ? 'loading' : isError ? 'error' : 'missing'}
  }

  return {data: selectReviewRequested(reviewers.pendingReviewRequests ?? [], viewerLogin), queryStatus: 'success'}
}
